import React, {useEffect} from "react";
import './VisualToggles.css'
import {useDispatch, useSelector} from "react-redux";
import {
  setQuarterZoneVisible,
  toggleBlockedCellsVisible,
  toggleCityOwnershipVisible,
  toggleFortressVisible,
  toggleGuildVisible,
  toggleHexCoordinatesVisible,
  toggleIncomeVisible,
  toggleQuarterZoneVisible,
  toggleTempleVisible,
  toggleUnitsVisible
} from "../../redux/actions";
import {
  selectBlockedCellsVisible,
  selectCityOwnershipVisible,
  selectFortressVisible,
  selectGuildVisible,
  selectHexCoordinatesVisible,
  selectIsTutorialActive,
  selectKeyPointIncomeVisible,
  selectQuarterZoneVisible,
  selectTempleVisible,
  selectUnitsVisible
} from "../../redux/reducers/privateStateReducer/selectors";
import {REGISTRY_SINGLETON} from "../../scenes/Registry";
import {usePrev} from "../../hooks/react/usePrev";
import {selectCurrentPlayer, selectPhase} from "../../redux/reducers/sharedStateReducer/selectors";
import {PHASES} from "../../constants/phases";
import {addClassNameIfTrue} from "../../utils/other/addClassNameIfTrue";
import {selectIsParallelSupplyPhase} from "../../utils/gameState/supply/parallelSupplySelectors";
import {GAME_BUTTON_SIZES, GameButton} from "../gameUi/GameButton";
import {GamePanel} from "../gameUi/GamePanel";
import {EngravedIcon} from "../gameUi/EngravedIcon";
import {VISUAL_TOGGLES_ICON} from "../../constants/visualToggles";
import {useCollapseOnCornerPanelRequest} from "../../hooks/visuals/useCornerPanels";

/**
 * Автопереключение слоев поля: смена хода возвращает скрытые отряды и
 * крепости, фаза Строительства включает зону кварталов. Живет отдельно от
 * кнопки слоев (она в углу над футером и перемонтируется при смене
 * раскладки) — иначе перемонтирование заново включало бы слои
 */
const QUARTER_ZONE_PHASES = [PHASES.SETUP, PHASES.CONSTRUCTION];

export function VisualTogglesAutoSwitch({isSpectatorMode = false}) {
  const dispatch = useDispatch();
  const unitsVisible = useSelector(selectUnitsVisible);
  const fortressVisible = useSelector(selectFortressVisible);
  const quarterZoneVisible = useSelector(selectQuarterZoneVisible);
  const phase = useSelector(selectPhase);
  const currentPlayer = useSelector(selectCurrentPlayer);

  const isParallelSupplyPhase = useSelector(selectIsParallelSupplyPhase);

  const currentPlayerId = currentPlayer ? currentPlayer.id : null;
  // Ключ смены хода. В одновременной фазе снабжения текущий игрок заморожен
  // (см. utils/gameState/supply/parallelSupply) и может не смениться ни на
  // входе в фазу, ни при ее закрытии — смену хода там отмечает сам вход в
  // фазу и выход из нее. Вне этой фазы ключ — по-прежнему id текущего игрока
  const turnKey = isParallelSupplyPhase ? `${phase}|${currentPlayerId}` : currentPlayerId;
  const prevTurnKey = usePrev(turnKey);
  const prevPhase = usePrev(phase);

  useEffect(() => {
    if (turnKey !== prevTurnKey) {
      if (!unitsVisible) {
        dispatch(toggleUnitsVisible());
      }

      if (!fortressVisible) {
        dispatch(toggleFortressVisible());
      }
    }
  }, [dispatch, turnKey, prevTurnKey, unitsVisible, fortressVisible]);

  useEffect(() => {
    if (phase === prevPhase) {
      return;
    }

    // В режиме зрителя оверлей не включается автоматически — только вручную
    // (кнопка переключателя ниже остаётся доступной)
    // Зона нужна там, где выбирают место под квартал: в строительстве и в расстановке
    const isZonePhase = QUARTER_ZONE_PHASES.includes(phase);
    if (isZonePhase && !quarterZoneVisible && !isSpectatorMode) {
      dispatch(setQuarterZoneVisible(true));
    } else if (!isZonePhase && QUARTER_ZONE_PHASES.includes(prevPhase) && quarterZoneVisible) {
      dispatch(setQuarterZoneVisible(false));
    }
  }, [dispatch, phase, prevPhase, quarterZoneVisible, isSpectatorMode]);

  return null;
}

/**
 * Кнопка слоев поля — в левом нижнем углу рядом с реакциями и чатом.
 * Устроена как панель реакций: якорь размером с кнопку, развернутая панель
 * выходит из потока и растет вверх-вправо от места кнопки
 */
export function VisualToggles({onExpandedChange, isCollapseRequested = false}) {
  const dispatch = useDispatch();
  const hexCoordinatesVisible = useSelector(selectHexCoordinatesVisible);
  const unitsVisible = useSelector(selectUnitsVisible);
  const incomeVisible = useSelector(selectKeyPointIncomeVisible);
  const cityOwnershipVisible = useSelector(selectCityOwnershipVisible);
  const blockedCellsVisible = useSelector(selectBlockedCellsVisible);
  const guildVisible = useSelector(selectGuildVisible);
  const templeVisible = useSelector(selectTempleVisible);
  const fortressVisible = useSelector(selectFortressVisible);
  const quarterZoneVisible = useSelector(selectQuarterZoneVisible);
  const isTutorialActive = useSelector(selectIsTutorialActive);

  const [isExpanded, setExpanded] = React.useState(false);


  const playToggleSound = () => {
    REGISTRY_SINGLETON.getSoundManager().playVisualToggleClick();
  }

  const onCityOwnershipClick = () => {
    playToggleSound();
    dispatch(toggleCityOwnershipVisible());
  }
  const onCoordinatesClick = () => {
    playToggleSound();
    dispatch(toggleHexCoordinatesVisible());
  }
  const onIncomeClick = () => {
    playToggleSound();
    dispatch(toggleIncomeVisible());
  }
  const onUnitsClick = () => {
    playToggleSound();
    dispatch(toggleUnitsVisible());
  }
  const onBlockedCellsClick = () => {
    playToggleSound();
    dispatch(toggleBlockedCellsVisible());
  }
  const onGuildClick = () => {
    playToggleSound();
    dispatch(toggleGuildVisible());
  }
  const onTempleClick = () => {
    playToggleSound();
    dispatch(toggleTempleVisible());
  }
  const onFortressClick = () => {
    playToggleSound();
    dispatch(toggleFortressVisible());
  }
  const onQuarterZoneClick = () => {
    playToggleSound();
    dispatch(toggleQuarterZoneVisible());
  }

  const onExpandToggleClick = () => {
    REGISTRY_SINGLETON.getSoundManager().playVisualTogglesExpand();
    setExpanded(!isExpanded)
  }

  // Развернутая панель поднимает свой угол (футер на телефоне) над боковыми
  // панелями — как панель реакций
  useEffect(() => {
    onExpandedChange?.(isExpanded);
  }, [isExpanded, onExpandedChange]);

  // Развернули другую панель угла — слои сворачиваются
  useCollapseOnCornerPanelRequest(isCollapseRequested, isExpanded, () => setExpanded(false));

  // Выключенный переключатель — приглушенная иконка (см. VisualToggles.css)
  const getToggleClassName = (isVisible) => isVisible ? undefined : 'visual-toggles_icon-off';

  const toggles = [
    {title: 'Зона кварталов', icon: '/img/icons/quarter_zone_black.png', isVisible: quarterZoneVisible, onClick: onQuarterZoneClick},
    {title: 'Заблокированные ячейки', icon: '/img/icons/block.png', isVisible: blockedCellsVisible, onClick: onBlockedCellsClick},
    {title: 'Координаты', icon: '/img/icons/coords.png', isVisible: hexCoordinatesVisible, onClick: onCoordinatesClick, className: 'visual-toggles_coords'},
    {title: 'Отряды', icon: '/img/icons/d6.png', isVisible: unitsVisible, onClick: onUnitsClick},
    {title: 'Крепости', icon: '/img/icons/fortress.png', isVisible: fortressVisible, onClick: onFortressClick},
    {title: 'Владельцы ячеек', icon: '/img/icons/bust.png', isVisible: cityOwnershipVisible, onClick: onCityOwnershipClick},
    {title: 'Храмы', icon: '/img/icons/temple_black.png', isVisible: templeVisible, onClick: onTempleClick},
    {title: 'Гильдии', icon: '/img/icons/guild_black.png', isVisible: guildVisible, onClick: onGuildClick},
    {title: 'Доход', icon: '/img/icons/coins black.png', isVisible: incomeVisible, onClick: onIncomeClick},
  ];

  return (
    // В обучении кнопка слоев скрыта (CSS): обучение ведет по своему сценарию
    <div
      className={`visual-toggles ${addClassNameIfTrue(!isExpanded, 'visual-toggles-collapsed')} ${addClassNameIfTrue(isTutorialActive, 'visual-toggles_tutorial')}`}
    >
      {!isExpanded ? (
        <GameButton
          size={GAME_BUTTON_SIZES.ICON}
          onClick={onExpandToggleClick}
          className={'visual-toggles_toggle-button'}
          title={'Слои поля'}
        >
          <EngravedIcon src={VISUAL_TOGGLES_ICON} size={24} alt={'Слои поля'}/>
        </GameButton>
      ) : (
        // Развернутая панель — лист базальта: слева значок слоев и кнопка
        // сворачивания на месте свернутой кнопки, справа сетка слоев
        <GamePanel shadow={true} className={'visual-toggles_panel'} data-toasts-avoid={true}>
          <div className={'visual-toggles_body'}>
            <div className={'visual-toggles_side'}>
              <EngravedIcon
                className={'visual-toggles_side-icon'}
                src={VISUAL_TOGGLES_ICON}
                size={25}
                alt={'Слои поля'}
              />
              <button
                onClick={onExpandToggleClick}
                className={'footer-heading-btn visual-toggles_collapse-button'}
                title={'Свернуть'}
              >
                <EngravedIcon src={'/img/icons/expand.png'} size={25} alt={'Свернуть'}/>
              </button>
            </div>

            <div className={'visual-toggles_grid'}>
              {toggles.map(({title, icon, isVisible, onClick, className}) => (
                <button
                  key={title}
                  onClick={onClick}
                  className={`footer-heading-btn ${className || ''}`}
                  title={title}
                >
                  <img
                    src={icon}
                    alt={title}
                    className={getToggleClassName(isVisible)}
                  />
                </button>
              ))}
            </div>
          </div>
        </GamePanel>
      )}
    </div>
  );
}
